/**
 * Which pill a billing package sits under on the Weekly Rollups tab —
 * Open · Sent · Approved.
 *
 * The package's own status is the ordinary answer: 'open' is collecting,
 * 'closed' has gone to the GC ("Sent"), 'approved' came back accepted. The
 * exception is the one that sends a PM hunting. When a late foreman PDF saves
 * into an already-sent week the server auto-reopens THAT WEEK — it is
 * `in_progress` again and its total has left Submitted-to-date until it is
 * resent — but the package around it is untouched and still reads 'closed'.
 * Bucketing on the package alone therefore filed live, unfinished work under
 * history, behind a pill the PM's own default view (Open) never shows.
 *
 * So: a package holding ANY in_progress week is Open, whatever its own status
 * says. Both 'closed' and 'approved' relocate — the GC accepting the earlier
 * version does not make the late row disappear, which is the same reason the
 * server auto-reopens an approved week at all.
 *
 * PRESENTATION ONLY, and that is load-bearing. Nothing here writes. A
 * relocated package keeps its real status, and every action offered on it
 * (Mark Approved, Approve & Send, Transfer Week, View details) is still
 * decided from that status — never from this bucket. Resending the reopened
 * week leaves no in_progress member, so the package falls back into its own
 * pile with no state change of any kind.
 */
import type { RollupStatus, SubmissionStatus } from '@coffman-tm/shared';

/** The three piles the count pills name. Same three values a submission's own
 *  status carries, because a package with nothing reopened lands on its own. */
export type PackageBucket = SubmissionStatus;

/** The shape this module needs off a SubmissionSummary — status plus enough of
 *  each member week to tell a live one from a finished one. */
export interface BucketablePackage {
  status: SubmissionStatus;
  rollups: readonly { status: RollupStatus }[];
}

/**
 * True when a member week is back in progress. On an OPEN package this is the
 * ordinary case (weeks collect before they are sent) and changes nothing; on a
 * closed or approved one it is the reopened-for-revision state, since closing
 * a package requires every member week to be sent.
 */
export function hasReopenedWeek(sub: BucketablePackage): boolean {
  return sub.rollups.some((r) => r.status === 'in_progress');
}

/** The pile this package renders under. */
export function packageBucketFor(sub: BucketablePackage): PackageBucket {
  return hasReopenedWeek(sub) ? 'open' : sub.status;
}
